import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Flame, Trophy, Target, Zap } from "lucide-react";

export default function Stats() {
  const { user, ready } = useAuth();
  const [s, setS] = useState(null);

  useEffect(() => {
    if (user) api.get("/stats/me").then((r) => setS(r.data)).catch(() => {});
  }, [user]);

  if (!ready) return <div className="p-10 text-slate-400">Loading…</div>;
  if (!user) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 px-4" data-testid="stats-signin">
        <h2 className="font-display text-3xl font-black uppercase">Sign in to track stats</h2>
        <p className="text-slate-400 mt-2">Create an account to sync progress across devices.</p>
        <Link to="/register" className="inline-block mt-5 px-6 py-3 bg-red-600 hover:bg-red-500 text-white font-bold uppercase rounded-xl">Create Account</Link>
      </div>
    );
  }
  if (!s) return <div className="p-10 text-slate-400">Loading stats…</div>;

  const stats = [
    { l: "Games Played", v: s.games_played, i: Target },
    { l: "Win %", v: `${(s.win_pct || 0).toFixed(0)}%`, i: Target },
    { l: "Current Streak", v: s.current_streak, i: Flame, cls: "text-orange-400" },
    { l: "Longest Streak", v: s.longest_streak, i: Flame },
    { l: "Total Score", v: (s.total_score || 0).toLocaleString(), i: Trophy, cls: "text-red-500" },
    { l: "Perfect 0.1s", v: s.perfect_first, i: Zap, cls: "text-emerald-400" },
    { l: "Theme Wins", v: `${s.theme_wins}/${s.theme_played}`, i: Trophy },
    { l: "Promo Wins", v: `${s.promo_wins}/${s.promo_played}`, i: Trophy },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      <div className="text-xs font-mono uppercase tracking-widest text-red-500 mb-1">Statistics</div>
      <h1 className="font-display text-3xl sm:text-5xl font-black uppercase text-white">Your Record</h1>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6" data-testid="stats-grid">
        {stats.map((st, i) => {
          const Ic = st.i;
          return (
            <Card key={i} className="bg-slate-900/70 border-slate-800 p-4">
              <Ic className={`h-4 w-4 ${st.cls || "text-slate-500"}`} />
              <div className="mt-2 font-mono text-2xl font-black">{st.v}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mt-1">{st.l}</div>
            </Card>
          );
        })}
      </div>

      {s.avg_interval != null && (
        <div className="mt-6 p-4 bg-slate-900/60 border border-slate-800 rounded-xl">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Average Reveal Interval</div>
          <div className="font-mono text-xl font-bold">
            Round {Math.round(s.avg_interval) + 1} of 6
          </div>
        </div>
      )}
    </div>
  );
}
